import React, { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "./onboarding/OnboardingPage.css";
import "./FinishStripePage.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

/**
 * Destination for talents whose login response carried `stripeRejected: true`
 * — i.e. their Stripe Connect account has a terminal disabled_reason (KYC
 * failure, sanctions list, prohibited industry, etc.). Part of the
 * mandatory-Stripe series. See server/lib/stripeLoginResume.js for the
 * rejection-allowlist classifier.
 *
 * Why this is a different page from FinishStripePage:
 *   - Rejected accounts can't recover by re-entering Stripe Onboarding — the
 *     account is terminal. Routing them to a "Continue setup" CTA would
 *     bounce them through Stripe and back with the same rejection.
 *   - Contact-support is the only forward path, so the CTA matches that.
 *   - We surface the raw `disabledReason` in muted grey for support diagnosis.
 *     The values in our allowlist (rejected.fraud, listed, etc.) are
 *     informational — not credentials or payloads.
 */
const StripeRejectedPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useContext(AuthContext);
  const location = useLocation();
  const code = (location.state as { code?: string } | null)?.code || null;

  const handleBack = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="finish-stripe-container">
      <div className="finish-stripe-inner">
        <div className="finish-stripe-back-row">
          <button
            type="button"
            className="step5-back"
            onClick={handleBack}
            aria-label={t("stripeRejectedPage.signOutAndReturn")}
          >
            <T k="stripeRejectedPage.back" tags={{ 0: (c) => <span aria-hidden="true">{c}</span> }} />
          </button>
        </div>

        <h1 className="finish-stripe-title">
          {firstName ? t("stripeRejectedPage.hi", { firstName }) : t("stripeRejectedPage.hi2")}
          <br />
          <span className="finish-stripe-title-accent stripe-rejected-title-accent">
            {t("stripeRejectedPage.weCouldnTVerify")}
          </span>
        </h1>

        <p className="finish-stripe-body">
          {t("stripeRejectedPage.stripeWasnTAble")}
        </p>

        <div className="step5-choice-grid finish-stripe-cta-grid">
          <a
            className="step5-choice step5-choice--stripe"
            href="mailto:support@requesta.app?subject=Account%20verification%20help"
          >
            <div className="step5-choice-main">
              <span className="step5-choice-icon" aria-hidden="true">
                {/* envelope icon */}
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <span className="step5-choice-body">
                <span className="step5-choice-title">{t("stripeRejectedPage.contactSupport")}</span>
                <span className="step5-choice-sub">
                  {t("stripeRejectedPage.supportRequestaApp")}
                </span>
              </span>
            </div>
          </a>
        </div>

        <p className="finish-stripe-support">
          {t("stripeRejectedPage.weTypicallyReplyWithin")}
        </p>

        {code && (
          <p className="stripe-rejected-code" aria-label={t("stripeRejectedPage.referenceCodeForSupport")}>
            {t("stripeRejectedPage.ref", { code })}
          </p>
        )}
      </div>
    </div>
  );
};

export default StripeRejectedPage;
